/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */
import {ActionTree} from 'vuex';
import {
  GameInterface,
  GamePlatformInterface,
  GamesStateInterface,
} from '@/store/modules/games/types';
import {RootState} from '@/store/types';
import {api} from '@/services/http';
import {AxiosError, AxiosResponse} from 'axios';

/**
 * Games Actions
 *
 * @author nchapman@frontier.co.uk
 */
export const actions: ActionTree<GamesStateInterface, RootState> = {
  /**
   * Fetch GamesNavigation
   *
   * @param dispatch
   * @param {any} commit
   */
  async fetchGames({dispatch, commit}: { commit: any, dispatch: any}): Promise<void> {
    return await api.axios.get('api/enabled-games', {
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {
      const payload: GameInterface[] = [];
      const data: any = response.data['hydra:member'];
      for (const game of data) {
        payload.push({
          id: game.id,
          name: game.name,
          slug: game.slug,
          platforms: game.platforms || [],
          versions: game.versions || [],
          featured: game.featured,
          enabled: game.enabled,
          categories: game.categories,
          readonly: game.readonly,
          use_loaded_via: game.use_loaded_via,
          branding: game.branding,
        });
      }
      commit('gamesLoaded', payload);
    }, (err: AxiosError) => {
      commit('gamesError');
    });
  },

  /**
   * Update current game version
   *
   * @param commit
   * @param version
   */
  updateCurrentGameVersion({commit}: { commit: any }, version: GameInterface ): void {
    commit('updateCurrentGameVersion', version);
  },

  /**
   * Update current game
   *
   * @param {any} commit
   * @param game
   */
  updateCurrentGame({commit}: { commit: any }, game: GameInterface | undefined): void {
    commit('updateCurrentGame', game);
  },

  /**
   * Fetch Platforms
   *
   * @param {any} commit
   */
  async fetchPlatforms({commit}: { commit: any }): Promise<GamePlatformInterface[]> {
    const payload: GamePlatformInterface[] = [];
    await api.axios.get('api/enabled-platforms', {
      params: {
        skipAuthorization: true,
      },
    }).then(
        async (response: AxiosResponse) => {
          const data: any = response.data['hydra:member'];
          for (const platform of data) {
            payload.push({
              acronym: '',
              id: platform.id,
              logo: '',
              prefix: platform.prefix,
              name: platform.name,
              slug: platform.slug,
              types: platform.types,
            });

            commit('updatePlatforms', payload);
      }
    });

    return payload;
  },
};
